import changelogApi from 'api/changelog/changelog.api';
import type {
  ChangeLogResponse,
  ChangelogPostRaw,
  InsideTimelyCategory,
} from 'api/changelog/changelog.api.types';
import { LAST_VIEWED_ANNOUNCEMENTS, LAST_VIEWED_CHANGELOG } from 'constants/user';
import { DateTime } from 'luxon';
import { saveUserProperty } from 'redux/modules/user.actions';
import type { XhrStatus } from 'redux/reusable/xhrStatus';
import {
  createXhrStatusModule,
  xhrStatusFactory,
  xhrStatusQueryInfo,
  xhrStatusUIState,
} from 'redux/reusable/xhrStatus';
import { getAccountCanAccessBeta } from 'redux/selectors/accounts';
import { getIsAppInitialized } from 'redux/selectors/global';
import { getIsRouteOnboarding } from 'redux/selectors/routing';
import { getUserProperties, getUserStore } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { Thunk } from 'types/redux';
import { actionCreatorFactory } from 'typescript-fsa';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { isAfter, isBefore } from 'utils/date-helper';

const actionCreator = actionCreatorFactory('timely/changelog');
const { withXhrStatusReducer, wrapFetching } = createXhrStatusModule('changelog');
const fetchedChangelog = actionCreator<ChangeLogResponse>('FETCHED_CHANGELOG');

export const CATEGORY_COMPANY_NEWS = 'company-news';
export const CATEGORY_PRODUCT_UPDATES = 'product-updates';
export const CATEGORY_DETAILED_CHANGELOG = 'changelog';
export const ANNOUNCEMENT_CATEGORIES: Array<InsideTimelyCategory> = [
  CATEGORY_COMPANY_NEWS,
  CATEGORY_PRODUCT_UPDATES,
];
export const MAX_ANNOUNCEMENT_POSTS = 9;
export const MAX_PRODUCT_UPDATE_POSTS = MAX_ANNOUNCEMENT_POSTS;
const MAX_DETAILED_CHANGELOG_POSTS = 20;

export interface State {
  xhrStatus: XhrStatus;
  entries?: Array<ChangelogPostRaw>;
}

export interface ChangelogPost {
  id: string;
  headline: string;
  date: string;
  summary: string;
  category: InsideTimelyCategory;
  url: string;
  image?: string;
  imageAlt: string;
  isImportant: boolean;
}

export const initialState: State = {
  xhrStatus: xhrStatusFactory(),
};

const reducer = withXhrStatusReducer(
  reducerWithInitialState(initialState).caseWithAction(fetchedChangelog, (state, action) => ({
    ...state,
    entries: action.payload.posts,
  }))
);

export default reducer;

export const fetchChangelog =
  (onlyAfterDate?: string): Thunk<Promise<void>> =>
  async (dispatch, getState) => {
    const fetchKey = `changelog-${onlyAfterDate || 'default'}`;
    const xhrStatus = xhrStatusQueryInfo(getState().changelog.xhrStatus, { key: fetchKey });

    if (!xhrStatus.shouldFetch) return;

    const result = await dispatch(
      wrapFetching(changelogApi.getReleaseNotes(onlyAfterDate), fetchKey)
    );
    if (result) {
      dispatch(fetchedChangelog(result));
    }
  };

export const markChangelogAsRead = (): Thunk => dispatch => {
  const value = DateTime.now().toISO();
  dispatch(saveUserProperty(LAST_VIEWED_CHANGELOG, value));
};

export const markAnnouncementsAsRead = (): Thunk => dispatch => {
  const value = DateTime.now().toISO();
  dispatch(saveUserProperty(LAST_VIEWED_ANNOUNCEMENTS, value));
};

const readChangelogEntries = (state: IState) => state.changelog.entries;
const readXhrStatus = (state: IState) => state.changelog.xhrStatus;

/**
 * Get all changelogs the workspace is allowed to see
 */
export const getChangelogEntries = createSelector(
  [readChangelogEntries, getAccountCanAccessBeta],
  (entries, isInBeta) => {
    return entries?.filter(({ only_visible_to_beta }) => {
      return !only_visible_to_beta || (only_visible_to_beta && isInBeta);
    });
  }
);

export const getAllChangelogPosts = createSelector(
  [getChangelogEntries],
  (entries): Array<ChangelogPost> => {
    return (
      entries
        ?.filter(item => item.category?.length > 0)
        .map<ChangelogPost>(item => {
          const { image, date, important_update, image_alt, ...rest } = item;
          return {
            ...rest,
            image: (image as string) || undefined, //  typeof image === 'string' ? image : undefined,
            date,
            isImportant: important_update || false,
            imageAlt: image_alt || item.headline,
          };
        }) || []
    );
  }
);

const sortByDateDesc = (a: ChangelogPost, b: ChangelogPost) => {
  if (isBefore(a.date, b.date)) {
    return 1;
  } else if (isAfter(a.date, b.date)) {
    return -1;
  } else {
    return 0;
  }
};

export const getProductUpdatePosts = createSelector([getAllChangelogPosts], posts => {
  return posts
    .filter(post => typeof post.image === 'string' && post.category === CATEGORY_PRODUCT_UPDATES)
    .sort(sortByDateDesc)
    .slice(0, MAX_PRODUCT_UPDATE_POSTS);
});

export const getAnnouncementPosts = createSelector([getAllChangelogPosts], posts => {
  return posts
    .filter(post => ANNOUNCEMENT_CATEGORIES.includes(post.category))
    .sort(sortByDateDesc)
    .slice(0, MAX_ANNOUNCEMENT_POSTS);
});

export const getDetailedChangelogPosts = createSelector(
  [getAllChangelogPosts],
  (posts): Array<ChangelogPost> => {
    return posts
      .filter(post => post.category === CATEGORY_DETAILED_CHANGELOG)
      .sort(sortByDateDesc)
      .slice(0, MAX_DETAILED_CHANGELOG_POSTS);
  }
);

export const getXhrUiState = createSelector([readXhrStatus], xhrStatus =>
  xhrStatusUIState(xhrStatus)
);

// When checking last viewed user property we need to account for loading state and never seen before state
enum Status {
  Loading, // We're still loading app, userProperties, or news from api
  NeverSeenBefore, // User has never seen this before, user property has not been set before
  LastRead, // User has read updates before
}

export interface LastReadResult {
  status: Status;
  date?: string;
}

export const getLastRead = createSelector(
  [getUserProperties, getIsAppInitialized, getXhrUiState],
  (userProperties, isAppReady, xhr): LastReadResult => {
    const isReady = isAppReady && xhr.hasInitialized && !xhr.isLoading;

    if (!isReady) {
      return {
        status: Status.Loading,
      };
    }

    const lastReadProp = userProperties[LAST_VIEWED_CHANGELOG];

    if (lastReadProp && typeof lastReadProp === 'string') {
      if (DateTime.fromISO(lastReadProp).isValid) {
        return {
          status: Status.LastRead,
          date: lastReadProp,
        };
      }
    }

    return {
      status: Status.NeverSeenBefore,
    };
  }
);

export const getLastReadAnnouncements = createSelector(
  [getUserProperties, getIsAppInitialized, getXhrUiState],
  (userProperties, isAppReady, xhr): LastReadResult => {
    const isReady = isAppReady && xhr.hasInitialized && !xhr.isLoading;

    if (!isReady) {
      return {
        status: Status.Loading,
      };
    }

    const lastReadProp = userProperties[LAST_VIEWED_ANNOUNCEMENTS];

    if (lastReadProp && typeof lastReadProp === 'string') {
      if (DateTime.fromISO(lastReadProp).isValid) {
        return {
          status: Status.LastRead,
          date: lastReadProp,
        };
      }
    }

    return {
      status: Status.NeverSeenBefore,
    };
  }
);

export const isAnnouncementPostUnread = (
  post: ChangelogPost,
  lastRead: LastReadResult,
  userCreatedTimestamp: number
): boolean => {
  if (!ANNOUNCEMENT_CATEGORIES.includes(post.category)) return false;
  if (lastRead.status === Status.Loading) return false;

  const userCreated = (DateTime.fromSeconds(userCreatedTimestamp) as DateTime<true>).toISODate();
  if (!isAfter(post.date, userCreated)) return false;

  if (lastRead.status === Status.LastRead && lastRead.date) {
    return isAfter(post.date, lastRead.date);
  }

  return lastRead.status === Status.NeverSeenBefore;
};

const getLatestUnreadPost = (
  posts: Array<ChangelogPost>,
  lastRead: LastReadResult,
  userCreatedTimestamp: number
): ChangelogPost | undefined => {
  return posts.find(post => isAnnouncementPostUnread(post, lastRead, userCreatedTimestamp));
};

export const getImportantProductUpdatePosts = createSelector([getAnnouncementPosts], posts => {
  return posts.filter(post => post.isImportant);
});

export const getLatestUnreadAnnouncement = createSelector(
  [getAnnouncementPosts, getLastReadAnnouncements, getUserStore],
  (posts, lastRead, user) => {
    return getLatestUnreadPost(posts, lastRead, user.created_at);
  }
);

export const getLatestUnreadProductUpdate = getLatestUnreadAnnouncement;

export const getLatestUnreadImportantProductUpdate = createSelector(
  [getImportantProductUpdatePosts, getLastReadAnnouncements, getUserStore],
  (posts, lastRead, user) => {
    return getLatestUnreadPost(posts, lastRead, user.created_at);
  }
);

export const getHasUnreadAnnouncements = createSelector(
  [getLatestUnreadAnnouncement],
  (latestAnnouncement): boolean => {
    return !!latestAnnouncement;
  }
);

export const getHasUnreadProductUpdates = getHasUnreadAnnouncements;

export const getHasUnreadImportantProductUpdate = createSelector(
  [getLatestUnreadImportantProductUpdate, getIsRouteOnboarding],
  (latestImportantProductUpdate, isRouteOnboarding): boolean => {
    return !!latestImportantProductUpdate && !isRouteOnboarding;
  }
);
